import { useEffect, useState } from 'react';

/**
 * Hook that detects and tracks changes in device pixel ratio (DPR).
 * Returns both the current DPR and a version counter for forcing remounts.
 * This is crucial for proper canvas rendering on high-DPI displays.
 *
 * The hook listens for changes in DPR which can occur when:
 * - Moving a window between displays with different pixel densities
 * - Fullscreening on a different monitor
 * - Browser zoom changes (Cmd/Ctrl +/-)
 *
 * @returns {{ dpr: number, version: number }} Current DPR and version counter
 */
export const useDevicePixelRatio = (): { dpr: number; version: number } => {
    const [version, setVersion] = useState<number>(0);
    const [dpr, setDpr] = useState<number>(() =>
        typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const updateDprAndVersion = (): void => {
            const newDpr = window.devicePixelRatio || 1;
            setDpr(newDpr);
            setVersion(v => v + 1);
        };

        // Listen for DPR changes via matchMedia - detects display changes AND browser zoom
        const mediaQuery = window.matchMedia(`(resolution: ${dpr}dppx)`);
        mediaQuery.addEventListener('change', updateDprAndVersion);

        // Listen for fullscreenchange - force recreation with delay for browser to update DPR
        const handleFullscreenChange = (): void => {
            // Use multiple delays to ensure DPR is updated
            setTimeout(updateDprAndVersion, 50);
            setTimeout(updateDprAndVersion, 200);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return (): void => {
            mediaQuery.removeEventListener('change', updateDprAndVersion);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, [dpr]); // Re-create media query listener when DPR changes

    return { dpr, version };
};
